import { store$ } from '@redux/store';
import LeadFormType from 'features/leadGenerators';
import { selectFavorites, selectIsProfileFavoritesLoaded, selectPendingSaves } from 'features/userProfile';
import { selectedNearbyListingCarousel } from 'features/searchResultsMap';
import { selectModal, selectUiState } from 'layout/redux/selectors';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, map, pairwise,
  withLatestFrom, fromEvent, combineLatest } from 'rxjs';
import RecommendedType from 'constants/RecommendedType';
import { selectConstructQueryString } from './selectors';

const modalTypesForModalSubmissionEvent = [
  LeadFormType.BrochureFormPaid.toString(),
  LeadFormType.InformationFormPaid.toString(),
  LeadFormType.InformationFormBasic.toString(),
  LeadFormType.AskQuestionFormPaid.toString(),
];

/**
 * getNewListingSaved: Gets the new listing that was saved.
 * @param {Array} previousFavorites Previous Favorite state.
 * @param {Array} currentFavorites Favorite state up-to-date.
 * @returns The favorite information of the new listing saved, or null if there's no new saved.
 */
function getNewListingSaved([previousFavorites, currentFavorites]) {
  const lastFavorited = currentFavorites[currentFavorites.length - 1];
  if (previousFavorites.includes(lastFavorited)) {
    return null;
  }

  return lastFavorited;
}

/**
 * Allows us to filter by id to search for the deleted id.
 * @param {any} prevList previous favorites list.
 * @param {any} newList new favorites list.
 * @returns Returns the new list with the ids removed.
 */
function findRemovedItems(prevList, newList) {
  const newIds = new Set(newList.map(item => item.listingId));
  return prevList.filter(prevItem => !newIds.has(prevItem.listingId));
}

/**
 * An observable that emits the selected marker from the store.
 */
const markerPinClicked$ = fromEvent(document.body, 'markerPinCLicked').pipe(
  map(event => event.detail.marker)
);

/**
 * An observable that emits clicks on whole document
 */
const pageWindowClick$ = fromEvent(document, 'click');

/**
 * An observable for keep searching button clicks in Saved Search.
 */
const onKeepSearchingButtonClicked$ = pageWindowClick$.pipe(
  map((event) => event.target.closest('[data-keep-searching-button]')),
  filter(Boolean));

/**
 * An observable for dashboard link clicks in Saved Search.
 */
const onDashboardLinkClicked$ = pageWindowClick$.pipe(
  map((event) => event.target.closest('[data-dashboard-link]')),
  filter(Boolean));

/**
 * An observable for save search button clicks.
 */
const onSavedSearchClicked$ = pageWindowClick$.pipe(
  filter((event) => event.target.closest('[data-saved-search]')));

/**
 * communityStaticMapClicked$ will emit every time user clicks in a community static map.
 */
const communityStaticMapClicked$ = fromEvent(document.body, 'communityStaticMapCLicked').pipe(
  map(event => ({
    deviceType: event.detail.deviceType,
    communityStaticMap: event.detail.communityStaticMap,
})));

/**
 * communityStaticMapDoubleClicked$ will emit every time user doubleclicks in a community static map.
 */
const communityStaticMapDoubleClicked$ = fromEvent(document.body, 'communityStaticMapDoubleCLicked').pipe(
  map(event => ({
    deviceType: event.detail.deviceType,
    communityStaticMap: event.detail.communityStaticMap,
})));

/**
 * communityStaticMapZoomChanged$ will emit every time a community static map adjusted to a new zoom level.
 */
const communityStaticMapZoomChanged$ = fromEvent(document.body, 'communityStaticMapZoom').pipe(
  map(event => ({
    zoomGesture: event.detail.zoomGesture,
    communityStaticMap: event.detail.communityStaticMap,
    deviceType: event.detail.deviceType,
    zoomLevel: event.detail.zoomLevel,
})));

/**
 * communityStaticMapHovered$ will emit new values every time a community static map was done being hovered. 
 */
const communityStaticMapHovered$ = fromEvent(document.body, 'communityStaticMapHover').pipe(
  map(event => ({
    hoverDuration: event.detail.hoverDuration,
    communityStaticMap: event.detail.communityStaticMap,
    deviceType: event.detail.deviceType,
})));

/**
 * An observable that emits the selected summary card from the store.
 */
const markerSummaryCardClicked$ = store$.pipe(map(selectedNearbyListingCarousel));

/**
 * Generates an observable from a summaryCardLinkClick Custom Event, then
 * bind the data for the active summary card to the summary card link clicked.
 */
const summaryCardLinkClicked$ = fromEvent(document, 'summaryCardLinkClick').pipe(
  map((event) => event?.detail?.summaryCard)
);

/**
 * Generates an observable from a 'summaryCardCtaClick' Custom Event, 
 * which is emitted when a user clicks on a summary card's call to action button.
 */
const summaryCardCtaClicked$ = fromEvent(document, "summaryCardCtaClick").pipe(
  map((event) => event.detail)
);

/**
 * An observable that emits the scroll event details when the nearby listings carousel is scrolled.
 */
const nearbyListingsCarouselScrolled$ = fromEvent(document, 'carouselScroll').pipe(
  map(event => event.detail),
);

/**
 * An observable that emits the details of the summary card close icon click event.
 */
const carouselCloseButtonClicked$ = fromEvent(document, "carouselClose").pipe(
  map(event => event.detail),
);

const pendingListingSaves$ = store$.pipe(
  map(selectPendingSaves),
  distinctUntilChanged(),
  pairwise(),
  map(([previousPendingListingSaves]) => previousPendingListingSaves)
);

// Observable created when there is a "pendingSaves" object in the redux store.
// This object contains a list of items that the user clicked the favorite button on but were not saved yet.
// (User not logged in).
const pendingListingSavesFavorites$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise()
);

const newListingSaved$ = store$.pipe(
  // We have to be sure when the Favorites from server were already loaded.
  filter(selectIsProfileFavoritesLoaded),
  map(selectFavorites),
  distinctUntilChanged(),
  pairwise(),
  map(getNewListingSaved),
  filter(Boolean),
  // We cannot work directly with the favorited information because it will have very simple information.
  // So, to get the listing information, we are going to consume pendingSaves property,
  // where the listing information is being saved by us. In this way we don't add more logic or information to the
  // favorited object that should be very basic as it is only a favorite.
  withLatestFrom(pendingListingSaves$),
  map(([newListingSaved, pendingListingSaves]) =>
    pendingListingSaves.find(
      (pendingSave) => Number(pendingSave.listingInformation.id) === Number(newListingSaved.listingId)
    )
  ),
  filter(Boolean)
);

// observable created when there is an add to the redux store object "pendingSaves".
// this object contains a list of items that the user clicked but are not already saved. (User not logged in)
// It will filter the actions to now when it changes.
const onPendingFavoritesChanged$ = store$.pipe(
  map(selectPendingSaves),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise(),
  map(([lastValue, newValue]) => {
    const newItems = newValue.filter((item) => !lastValue.includes(item));
    return newItems;
  }),
  filter((newItems) => newItems.length > 0),
  map((newItems) => newItems[newItems.length - 1])
);

// Observable that emits when an item has been removed from the favorites list.
const removedFavorites$ = store$.pipe(
  filter(selectIsProfileFavoritesLoaded),
  map(selectFavorites),
  filter(Boolean),
  distinctUntilChanged(),
  pairwise(),
  map(([lastValue, newValue]) => findRemovedItems(lastValue, newValue)),
  filter((removedItems) => removedItems.length > 0),
  map((removedItems) => removedItems[removedItems.length - 1])
);

const paidCommunityModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'community'),
  filter((modalInformation) => modalInformation.listingInformation.isBasic === 'false'),
  filter((modalInformation) => !modalInformation.listingInformation.isMasterPlanCommunity),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const basicCommunityModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'community'),
  filter((modalInformation) => modalInformation.listingInformation.isBasic === 'true'),
  filter((modalInformation) => !modalInformation.listingInformation.isMasterPlanCommunity),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const paidMasterPlanCommunityModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'community'),
  filter((modalInformation) => modalInformation.listingInformation.isMasterPlanCommunity === 'true'),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

/**
 * An observable that emits when the user clicks on the "Save Search" button 
 * and the saved search modal gets displayed.
 */
const createdSaveSearchModalShown$ = store$.pipe(
  map(selectModal),
  distinctUntilChanged(),
  filter(Boolean),
  filter((modal) => modal.name === 'createdSaveSearch'));

const recommendedCommunitiesSubmitted$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  distinctUntilKeyChanged('previousName'),
  filter(
    (modalInformation) =>
      modalInformation.type === 'community' || modalInformation.type === 'spec' || modalInformation.type === 'plan'
  ),
  filter((modalInformation) => modalInformation.previousName === 'recommendedScreen'),
  filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
  distinctUntilChanged()
);

const recommendedCommunitiesSubmittedBasic$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  distinctUntilKeyChanged('previousName'),
  filter(
    (modalInformation) =>
      modalInformation.type === 'community' || 
      modalInformation.type === 'spec' || 
      modalInformation.type === 'plan' ||
      modalInformation.type === 'basicListing'
  ),
  filter((modalInformation) => modalInformation.previousName === 'recommendedScreenBasic'),
  filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
  distinctUntilChanged()
);

const recommendedCommunitiesShow$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  filter(
    (modalInformation) =>
      modalInformation.name === RecommendedType.RecommendedPaid().getName() ||
      modalInformation.name === RecommendedType.RecommendedBasic().getName()
  ),
  distinctUntilChanged()
);

const paidHomeModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'spec' || modalInformation.type === 'plan'),
  filter((modalInformation) => modalInformation.variation !== 'basic'),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const basicPlanModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'plan' ),
  filter((modalInformation) => modalInformation.variation === 'basic'),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const basicSpecModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'spec'),
  filter((modalInformation) => modalInformation.variation === 'basic'),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const basicListingModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'basicListing'),
  filter((modalInformation) => modalInformation.variation === 'basic'),
  filter((modalInformation) => modalTypesForModalSubmissionEvent.includes(modalInformation.leadGeneratorOpener))
);

const paidManufacturedLocationModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'community'),
  filter(
    (modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.ManufacturedBrochureFormPaid.toString()
  )
);

const paidManufacturedPlanModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'plan'),
  filter(
    (modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.ManufacturedBrochureFormPaid.toString()
  )
);

const paidManufacturedSpecModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'spec'),
  filter(
    (modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.ManufacturedBrochureFormPaid.toString()
  )
);

const paidAskAQuestionLocationModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'community'),
  filter((modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.AskQuestionFormPaid.toString())
);

const paidAskAQuestionPlanModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'plan'),
  filter((modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.AskQuestionFormPaid.toString())
);

const paidAskAQuestionSpecModalSubmission$ = store$.pipe(
  map(selectUiState),
  distinctUntilKeyChanged('isFetchingModal'),
  filter((uiState) => uiState.isFetchingModal),
  map((uiState) => uiState.modalInformation),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  filter((modalInformation) => modalInformation.type === 'spec'),
  filter((modalInformation) => modalInformation.leadGeneratorOpener === LeadFormType.AskQuestionFormPaid.toString())
);

/**
 * An observable that emits an event when the 'openMarkerUrl' event is triggered on the document body.
 * The observable filters the events to only include those where the marker Id is present in the event detail.
 */
const urlOpened$ = fromEvent(document.body, "openMarkerUrl").pipe(
  filter((event) => Boolean(event?.detail?.marker?.id))
);


/**
 * Emits an observable that combines the `markerClicked$` and `urlOpened$` observables,
 * and filters the result to only include events where the marker ID matches the event's marker ID.
 * The emitted value includes the interaction type, the marker, and the timestamp of the event.
 * The observable will only emit distinct values based on the timestamp. This is done in order to prevent
 * triggering multiple times for the same interaction.
 */
const markerUrlOpened$ = combineLatest([markerPinClicked$, urlOpened$]).pipe(
  filter(([marker, event]) => marker?.id === event?.detail?.marker?.id),
  map(([marker, event]) => ({
    interactionType: event?.detail.interactionType,
    marker,
    timeStamp: event.timeStamp,
  })),
  distinctUntilKeyChanged("timeStamp")
);

/**
 * An observable that emits when the modal close button is clicked.
 * The emitted event contains the modal information in the `detail` property.
 */
const modalCloseButtonClicked$ = fromEvent(document.body, "modalClosed").pipe(
  filter((event) => Boolean(event?.detail?.modalInformation))
);

/**
* This observable will stream any time the seach query string changes.
*/
const onQueryStringChanged$ = store$.pipe(
 map(selectConstructQueryString),
 distinctUntilChanged(),
);

export {
  newListingSaved$,
  paidCommunityModalSubmission$,
  basicCommunityModalSubmission$,
  paidMasterPlanCommunityModalSubmission$,
  recommendedCommunitiesSubmitted$,
  recommendedCommunitiesSubmittedBasic$,
  paidHomeModalSubmission$,
  basicPlanModalSubmission$,
  basicSpecModalSubmission$,
  basicListingModalSubmission$,
  paidManufacturedLocationModalSubmission$,
  paidManufacturedPlanModalSubmission$,
  paidManufacturedSpecModalSubmission$,
  paidAskAQuestionLocationModalSubmission$,
  paidAskAQuestionPlanModalSubmission$,
  paidAskAQuestionSpecModalSubmission$,
  markerPinClicked$,
  markerSummaryCardClicked$,
  summaryCardLinkClicked$,
  summaryCardCtaClicked$,
  markerUrlOpened$,
  modalCloseButtonClicked$,
  onQueryStringChanged$,
  onPendingFavoritesChanged$,
  pendingListingSavesFavorites$,
  removedFavorites$,
  nearbyListingsCarouselScrolled$,
  carouselCloseButtonClicked$,
  communityStaticMapClicked$,
  communityStaticMapDoubleClicked$,
  communityStaticMapZoomChanged$,
  communityStaticMapHovered$,
  recommendedCommunitiesShow$,
  pageWindowClick$,
  createdSaveSearchModalShown$,
  onDashboardLinkClicked$,
  onKeepSearchingButtonClicked$,
  onSavedSearchClicked$,
};
